<!-- Column: Actions -->
<template>
  <div class="d-flex align-items-center justify-content-center">
    <!-- Debug Info -->
    <div v-if="false" class="debug-info">
      <pre>{{ routePermissions }}</pre>
    </div>

    <!-- View Action -->
    <b-button
      v-if="hasViewAction"
      variant="flat-primary"
      class="btn-icon rounded-circle"
      v-b-tooltip.hover.noninteractive
      :title="
        hasViewAction && !canView
          ? 'You don\'t have permission to view this item'
          : `View ${singularResource}`
      "
      @click="canView ? $emit('viewAction') : null"
      :disabled="!canView"
      :class="{ 'disabled-action': hasViewAction && !canView }"
    >
      <feather-icon icon="EyeIcon" size="16" />
    </b-button>

    <!-- Edit Action -->
    <b-button
      v-if="hasEditAction"
      variant="flat-info"
      class="btn-icon rounded-circle"
      v-b-tooltip.hover.noninteractive
      :title="
        hasEditAction && !canEdit
          ? 'You don\'t have permission to edit this item'
          : `Edit ${singularResource}`
      "
      @click="canEdit ? $emit('editAction') : null"
      :disabled="!canEdit"
      :class="{ 'disabled-action': hasEditAction && !canEdit }"
    >
      <feather-icon icon="EditIcon" size="16" />
    </b-button>

    <!-- Delete Action -->
    <b-button
      v-if="hasDeleteAction"
      variant="flat-danger"
      class="btn-icon rounded-circle"
      v-b-tooltip.hover.noninteractive
      :title="
        hasDeleteAction && !canDelete
          ? 'You don\'t have permission to delete this item'
          : `Delete ${singularResource}`
      "
      @click="canDelete ? $emit('deleteAction') : null"
      :disabled="!canDelete || loadingDelete"
      :class="{ 'disabled-action': hasDeleteAction && !canDelete }"
    >
      <template v-if="loadingDelete">
        <b-spinner small />
      </template>
      <template v-else>
        <feather-icon icon="TrashIcon" size="16" />
      </template>
    </b-button>

    <slot style="z-index: 1000 !important" />
  </div>
</template>

<script>
import { computedResourceSingular, validateResource } from '@/utils/functions';
import { BButton, BModal, BSpinner, VBTooltip } from 'bootstrap-vue';
import { mapState } from 'vuex';

export default {
  name: 'ActionCell',

  components: {
    BButton,
    BModal,
    BSpinner,
    featherIcon: () => import('@core/components/feather-icon/FeatherIcon.vue'),
  },

  directives: {
    'b-tooltip': VBTooltip,
  },

  props: {
    // Name of the resource (e.g., "Customer", "Internal Delivery")
    resource: {
      type: Object,
      required: true,
      validator(value) {
        return validateResource(value);
      },
    },
    // Optionally can pass a route object directly
    routeObject: {
      type: Object,
      default: null,
    },
    // Control which actions to show
    hasViewAction: {
      type: Boolean,
      default: false,
    },
    hasEditAction: {
      type: Boolean,
      default: false,
    },
    hasDeleteAction: {
      type: Boolean,
      default: false,
    },
    // Loading state for delete action
    loadingDelete: {
      type: Boolean,
      default: false,
    },
    // Custom button variants
    viewVariant: {
      type: String,
      default: 'flat-primary',
    },
    editVariant: {
      type: String,
      default: 'flat-primary',
    },
    deleteVariant: {
      type: String,
      default: 'flat-primary',
    },
  },

  emits: ['viewAction', 'editAction', 'deleteAction'],

  data() {
    return {
      isDeleteModalVisible: false,
    };
  },

  computed: {
    ...mapState('user', ['userRights', 'authRoutes']),

    singularResource() {
      return computedResourceSingular(this.resource);
    },

    // Get the route name for this resource
    resourceRouteName() {
      // If it has a direct route property
      if (this.resource.route) {
        return this.resource.route;
      }

      // If it's from RESOURCES constant
      if (this.resource.routeName) {
        return this.resource.routeName;
      }

      return '';
    },

    // Check permissions based on resource route
    routePermissions() {
      // If routeObject was directly passed, use it (direct permissions object from parent)
      if (this.routeObject) {
        return this.routeObject;
      }

      // If resource has direct permissions (like in your example)
      if (
        this.resource.view !== undefined ||
        this.resource.edit !== undefined ||
        this.resource.delete !== undefined
      ) {
        return this.resource;
      }

      // Check if this route exists in authRoutes (main structure)
      const routeName = this.resourceRouteName;
      if (routeName && this.authRoutes && this.authRoutes[routeName]) {
        return this.authRoutes[routeName];
      }

      // No permissions found, return defaults (rely on props)
      return {
        view: true,
        add: true,
        edit: true,
        delete: true,
      };
    },

    canView() {
      return this.hasViewAction && this.routePermissions.view === true;
    },

    canEdit() {
      return this.hasEditAction && this.routePermissions.edit === true;
    },

    canDelete() {
      return this.hasDeleteAction && this.routePermissions.delete === true;
    },

    // Check if any actions are available to display
    hasAnyAction() {
      return this.hasViewAction || this.hasEditAction || this.hasDeleteAction;
    },
  },
};
</script>

<style lang="scss" scoped>
.btn-icon {
  padding: 0.5rem;
  &:hover {
    transform: translateY(-2px);
  }
}

.disabled-action {
  opacity: 0.6;
  cursor: not-allowed;

  &:hover {
    transform: none;
  }
}

/* Global styles for the tooltips (to be more visible) */
::v-deep {
  .tooltip {
    opacity: 1 !important;

    .tooltip-inner {
      background-color: rgba(0, 0, 0, 0.85);
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
      max-width: 250px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  }
}
</style>
